import { Alert, AlertTitle, Button, Typography } from '@mui/material';

import { API_BASE_URL, ApiNetworkError, ApiRequestError } from '../api/client';

/**
 * The one place an error becomes something an operator reads.
 *
 * The rule it enforces is BR-13 and BR-28 applied to the back office: **nothing reaches this screen unless
 * somebody wrote it in Slovak for a person.** The API's messages qualify — R0033 put them in the message
 * catalogue. A browser's `TypeError`, a stringified object, or a stack does not, and rendering `.message`
 * from an arbitrary `Error` is how `Failed to fetch` ended up in front of an operator who then reported
 * being unable to log in.
 *
 * **`onRetry` is optional and should be passed wherever the request can simply be made again** (R0122).
 * Ten pages handled loading and error and not one offered a way to try again, so a staff member whose
 * request failed reloaded the browser. The action lives here rather than in each page for the same reason
 * the copy does: one place decides what a failure looks like, and a page added tomorrow inherits it.
 */
export const ErrorNotice = ({ error, onRetry }: { error: unknown; onRetry?: () => void }) => {
  if (!error) {
    return null;
  }

  const retry =
    onRetry === undefined ? null : (
      <Button size="small" color="inherit" onClick={onRetry} sx={{ mt: 1, display: 'block' }}>
        Skúsiť znova
      </Button>
    );

  if (error instanceof ApiNetworkError) {
    return (
      <Alert severity="warning" variant="outlined" sx={{ mb: 2.5 }}>
        <AlertTitle sx={{ mb: 0.25 }}>Server neodpovedá</AlertTitle>
        Back office je nastavený na <code>{API_BASE_URL}</code>. Skontrolujte, či tam admin API beží, a skúste
        to znova. Toto nie je chyba prihlasovacích údajov.
        {retry}
      </Alert>
    );
  }

  if (error instanceof ApiRequestError) {
    return (
      <Alert severity="error" variant="outlined" sx={{ mb: 2.5 }}>
        <AlertTitle sx={{ mb: 0.25 }}>Chyba {error.status}</AlertTitle>
        {error.message}
        {error.correlationId ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            correlation id {error.correlationId}
          </Typography>
        ) : null}
        {retry}
      </Alert>
    );
  }

  // Anything else is a defect in this application rather than a message for anybody. Say so plainly and
  // put the detail where a developer will find it, instead of printing an English string at an operator.
  // eslint-disable-next-line no-console
  console.error('[bo-admin] Unhandled error surfaced to the UI:', error);

  return (
    <Alert severity="error" variant="outlined" sx={{ mb: 2.5 }}>
      <AlertTitle sx={{ mb: 0.25 }}>Neočakávaná chyba</AlertTitle>
      Niečo sa pokazilo na strane back officeu. Podrobnosti sú v konzole prehliadača.
      {retry}
    </Alert>
  );
};
